import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const THEME_STORAGE_KEY = "redash_theme";
const VALID_THEMES = ["light", "dark"];

const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
});

/**
 * Read the `theme` URL parameter from the current page URL.
 * Returns "dark", "light", or null if not present/invalid.
 */
function getThemeFromURL() {
  try {
    const params = new URLSearchParams(window.location.search); // eslint-disable-line compat/compat
    const urlTheme = params.get("theme");
    if (urlTheme && VALID_THEMES.includes(urlTheme)) {
      return urlTheme;
    }
  } catch (e) {
    // ignore URL parsing errors
  }
  return null;
}

/**
 * Read the saved theme from localStorage.
 * Returns "dark", "light", or null if not present/invalid.
 */
function getThemeFromStorage() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && VALID_THEMES.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // ignore storage errors (e.g. private browsing)
  }
  return null;
}

/**
 * Detect system theme preference using prefers-color-scheme media query.
 * Returns "dark", "light", or null if not supported.
 */
function getSystemTheme() {
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return "dark";
    }
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return "light";
    }
  } catch (e) {
    // ignore media query errors
  }
  return null;
}

/**
 * Apply or remove the `theme-dark` class on document.body.
 */
function applyThemeToBody(theme) {
  if (theme === "dark") {
    document.body.classList.add("theme-dark");
    document.body.style.backgroundColor = "#0a0e14";
  } else {
    document.body.classList.remove("theme-dark");
    document.body.style.backgroundColor = "#ffffff";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    // Priority: URL param > localStorage > system preference > default (dark)
    const urlTheme = getThemeFromURL();
    if (urlTheme) return urlTheme;

    const storedTheme = getThemeFromStorage();
    if (storedTheme) return storedTheme;

    const systemTheme = getSystemTheme();
    if (systemTheme) return systemTheme;

    // Default to dark theme to match portal and avoid white flash
    return "dark";
  });

  const setTheme = useCallback((newTheme) => {
    if (VALID_THEMES.includes(newTheme)) {
      // 切换前禁用所有 transition，避免闪烁
      document.body.classList.add("theme-switching");
      
      setThemeState(newTheme);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      } catch (e) {
        // ignore storage errors
      }

      // 切换完成后恢复 transition，并触发 resize 让 Plotly 图表重新计算尺寸
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.body.classList.remove("theme-switching");
          // 延迟触发 resize 事件，确保 CSS 已完全应用，
          // Plotly 的 resizeObserver 会在下一个 100ms 轮询周期检测到变化并重绘
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
          }, 150);
        });
      });
    }
  }, []);

  // Apply theme class to body whenever theme changes
  useEffect(() => {
    applyThemeToBody(theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    try {
      const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = (e) => {
        // Only auto-switch if user hasn't manually set a preference
        const storedTheme = getThemeFromStorage();
        if (!storedTheme) {
          setTheme(e.matches ? "dark" : "light");
        }
      };

      // Modern browsers
      if (darkModeQuery.addEventListener) {
        darkModeQuery.addEventListener('change', handleSystemThemeChange);
        return () => darkModeQuery.removeEventListener('change', handleSystemThemeChange);
      }
      // Legacy browsers
      else if (darkModeQuery.addListener) {
        darkModeQuery.addListener(handleSystemThemeChange);
        return () => darkModeQuery.removeListener(handleSystemThemeChange);
      }
    } catch (e) {
      // ignore media query errors
    }
  }, [setTheme]);

  // Listen for PostMessage from parent (Cube Portal) for runtime theme switching
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = event.data;
        if (data && data.type === "theme-change" && VALID_THEMES.includes(data.theme)) {
          setTheme(data.theme);
        }
      } catch (e) {
        // ignore malformed messages
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeProvider;
